import { useMemo, useRef } from 'react';
import {
	Combobox,
	ComboboxChip,
	ComboboxChips,
	ComboboxChipsInput,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxItem,
	ComboboxList,
	ComboboxValue,
} from '../combobox';
import { SdgIcon } from '../../custom/sdg-icons';
import { cn } from '../../lib/utils';
import { hasFieldError, useFieldContext, useIsFieldOptional } from './shared';
import { useFormReadOnly } from './form-read-only';
import { FieldLabelRow } from './field-label-row';

type SdgOption = { value: string; label: string };

const defaultSdgNames: Record<number, string> = {
	1: 'No Poverty',
	2: 'Zero Hunger',
	3: 'Good Health and Well-being',
	4: 'Quality Education',
	5: 'Gender Equality',
	6: 'Clean Water and Sanitation',
	7: 'Affordable and Clean Energy',
	8: 'Decent Work and Economic Growth',
	9: 'Industry, Innovation and Infrastructure',
	10: 'Reduced Inequalities',
	11: 'Sustainable Cities and Communities',
	12: 'Responsible Consumption and Production',
	13: 'Climate Action',
	14: 'Life Below Water',
	15: 'Life on Land',
	16: 'Peace, Justice and Strong Institutions',
	17: 'Partnerships for the Goals',
};

export default function SdgField(props: {
	label?: string;
	info?: React.ReactNode;
	sdgNames?: Record<number, string>;
	searchPlaceholder?: string;
	noResultsText?: string;
}) {
	const field = useFieldContext<number[]>();
	const formReadOnly = useFormReadOnly();
	const isOptional = useIsFieldOptional(field.name);
	const chipsRef = useRef<HTMLDivElement>(null);
	const names = props.sdgNames ?? defaultSdgNames;

	const sdgOptions = useMemo<SdgOption[]>(() => {
		return Array.from({ length: 17 }, (_, i) => ({
			value: String(i + 1),
			label: `${i + 1}. ${names[i + 1]!}`,
		}));
	}, [names]);

	const selectedItems = useMemo(
		() => sdgOptions.filter((o) => field.state.value.includes(Number(o.value))),
		[sdgOptions, field.state.value],
	);

	const labelByValue = useMemo(() => new Map(sdgOptions.map((o) => [o.value, o.label])), [sdgOptions]);

	return (
		<div>
			{props.label && (
				<FieldLabelRow
					label={props.label}
					htmlFor={field.name}
					hasError={field.state.meta.isTouched && !field.state.meta.isValid}
					optional={isOptional}
					info={props.info}
					className="mb-2"
				/>
			)}
			<div
				className={cn(
					formReadOnly &&
						'pointer-events-none [&_[data-slot=combobox-chips]]:border-transparent [&_[data-slot=combobox-chips]]:px-0 [&_[data-slot=combobox-chips]]:focus-within:ring-0 [&_[data-slot=combobox-chip-input]]:min-w-0 [&_[data-slot=combobox-chip-input]]:flex-[0] [&_[data-slot=combobox-chip-input]]:opacity-0 [&_[data-slot=combobox-chip-input]]:overflow-hidden [&_[data-slot=combobox-chip-remove]]:opacity-0 [&_[data-slot=combobox-chip-remove]]:w-0 [&_[data-slot=combobox-chip-remove]]:-ml-2 [&_[data-slot=combobox-chip]]:h-auto [&_[data-slot=combobox-chip]]:px-0',
				)}
			>
				<Combobox
					modal
					multiple
					items={sdgOptions}
					value={selectedItems}
					onValueChange={(items: SdgOption[]) => {
						field.handleChange(items.map((i) => Number(i.value)));
					}}
					isItemEqualToValue={(a: SdgOption, b: SdgOption) => a.value === b.value}
				>
					<ComboboxChips ref={chipsRef} className="gap-3">
						<ComboboxValue>
							{field.state.value.filter(Boolean).map((v) => (
								<ComboboxChip key={v} className="gap-2 bg-transparent px-0 text-sm">
									<SdgIcon goal={v} viewBox="0 120 404 285" className="w-8 shrink-0 rounded-sm" />
									{labelByValue.get(String(v)) ?? v}
								</ComboboxChip>
							))}
						</ComboboxValue>
						<ComboboxChipsInput
							id={field.name}
							aria-invalid={hasFieldError(field.state.meta)}
							placeholder={props.searchPlaceholder ?? 'Search…'}
						/>
					</ComboboxChips>
					<ComboboxContent anchor={chipsRef}>
						<ComboboxEmpty>{props.noResultsText ?? 'No results found.'}</ComboboxEmpty>
						<ComboboxList>
							{(item: SdgOption) => (
								<ComboboxItem key={item.value} value={item}>
									<SdgIcon goal={Number(item.value)} viewBox="0 120 404 285" className="w-6! shrink-0 rounded-sm" />
									{item.label}
								</ComboboxItem>
							)}
						</ComboboxList>
					</ComboboxContent>
				</Combobox>
			</div>
		</div>
	);
}
